import React from 'react';
import { formatNumber } from '../../utils/helpers';
import './WalletPositionsTable.css';

/**
 * Format leverage: 10.5x → 10x
 */
const formatLeverage = (leverage) => {
  if (!leverage) return '-';
  return `${Math.round(leverage)}x`;
};

/**
 * Wallet Positions Table Component
 * Shows current positions for a specific wallet
 * Following grey-black theme
 */
function WalletPositionsTable({ positions, loading }) {
  if (loading) {
    return (
      <div className="card wallet-positions-card">
        <div className="card-header">
          <h6 className="mb-0">Active Positions</h6>
        </div>
        <div className="card-body">
          <div className="text-center py-5">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <div className="card wallet-positions-card">
        <div className="card-header">
          <h6 className="mb-0">Active Positions</h6>
        </div>
        <div className="card-body">
          <p className="text-center text-muted py-5">No active positions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card wallet-positions-card">
      <div className="card-header">
        <h6 className="mb-0">Active Positions ({positions.length})</h6>
      </div>

      {/* Table Header */}
      <div className="wallet-positions-header">
        <div className="header-symbol">Symbol</div>
        <div className="header-side">Side</div>
        <div className="header-size">Size</div>
        <div className="header-leverage">Leverage</div>
        <div className="header-entry">Entry Price</div>
        <div className="header-mark">Mark Price</div>
        <div className="header-pnl">PnL</div>
        <div className="header-liquidation">Liq. Price</div>
      </div>

      <div className="card-body p-0">
        {positions.map((position, index) => {
          const isProfitable = position.unrealizedPnl >= 0;
          const pnlClass = isProfitable ? 'positive' : 'negative';

          return (
            <div key={index} className="wallet-position-row">
              <div className="cell-symbol">
                <span className="symbol-text">{position.symbol}</span>
              </div>

              <div className={`cell-side ${position.side === 'long' ? 'long' : 'short'}`}>
                <span className="side-badge">{position.side.toUpperCase()}</span>
              </div>

              <div className="cell-size">
                <span className="size-value">${formatNumber(position.positionValue)}</span>
                <span className="size-label">{position.size?.toFixed(3) || '-'} {position.symbol}</span>
              </div>

              <div className="cell-leverage">
                <span className="leverage-badge">{formatLeverage(position.leverage)}</span>
              </div>

              <div className="cell-entry">
                <span className="price-value">${position.entryPrice?.toFixed(2) || '-'}</span>
              </div>

              <div className="cell-mark">
                <span className="price-value">${position.markPrice?.toFixed(2) || '-'}</span>
              </div>

              <div className={`cell-pnl ${pnlClass}`}>
                <span className="pnl-value">
                  {isProfitable ? '+' : ''}{formatNumber(position.unrealizedPnl)}
                </span>
              </div>

              <div className="cell-liquidation">
                <span className="liq-price">${position.liquidationPrice?.toFixed(2) || '-'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WalletPositionsTable;
